import { useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { fbTrack, gaPageView } from "@/lib/track";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Toaster } from "@/components/ui/sonner";

import Home from "@/pages/Home";
import Coverage from "@/pages/Coverage";
import WavFiber from "@/pages/WavFiber";
import Streaming from "@/pages/Streaming";
import Consulting from "@/pages/Consulting";
import Business from "@/pages/Business";
import Contact from "@/pages/Contact";
import Survey from "@/pages/Survey";
import Billing from "@/pages/Billing";
import PayBill from "@/pages/PayBill";
import Feedback from "@/pages/Feedback";
import ThankYou from "@/pages/ThankYou";
import Admin from "@/pages/Admin";
import AdminLogin from "@/pages/AdminLogin";
import NotFound from "@/pages/NotFound";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

// Meta Pixel: fire PageView on SPA route changes (base code fires the first one),
// and track clicks on phone / text / email links as Contact events.
function PixelTracker() {
  const { pathname } = useLocation();
  const first = useRef(true);

  useEffect(() => {
    if (first.current) { first.current = false; return; }
    fbTrack("PageView");
    gaPageView(pathname);
  }, [pathname]);

  useEffect(() => {
    const onClick = (e) => {
      const link = e.target.closest && e.target.closest("a[href]");
      if (!link) return;
      const href = link.getAttribute("href") || "";
      if (/^(tel:|sms:|mailto:)/i.test(href)) {
        fbTrack("Contact", { method: href.split(":")[0].toLowerCase() });
      }
    };
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, []);

  return null;
}

function SmoothScroll({ children }) {
  const lenisRef = useRef(null);
  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return;
    const lenis = new Lenis({ duration: 1.1, smoothWheel: true, lerp: 0.09 });
    lenisRef.current = lenis;
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);
  return children;
}

function App() {
  return (
    <div className="App">
      <div className="noise-overlay" aria-hidden="true" />
      <BrowserRouter>
        <SmoothScroll>
          <ScrollToTop />
          <PixelTracker />
          <Navbar />
          <main>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/coverage" element={<Coverage />} />
              <Route path="/wavfiber" element={<WavFiber />} />
              <Route path="/streaming" element={<Streaming />} />
              <Route path="/wifi-consulting" element={<Consulting />} />
              <Route path="/business" element={<Business />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/survey" element={<Survey />} />
              <Route path="/update" element={<Billing />} />
              <Route path="/pay" element={<PayBill />} />
              <Route path="/pay.php" element={<PayBill />} />
              <Route path="/feedback" element={<Feedback />} />
              <Route path="/thank-you" element={<ThankYou />} />
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin" element={<Admin />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </main>
          <Footer />
        </SmoothScroll>
      </BrowserRouter>
      <Toaster position="top-center" theme="dark" />
    </div>
  );
}

export default App;
